/*
 * Stockholm Fragrance CAIA-style checkout V7.7 — Shopify Markets sync + live shipping threshold refresh + expanded markets + per-country delivery memory
 * Destination whitelist is based on the CAIA reference list plus approved Stockholm Fragrance markets.
 * Only destinations also enabled in Shopify Markets are shown; Shopify still validates real shipping availability/rates.
 */
(() => {
  const root = document.querySelector('[data-sf-checkout]');
  if (!root || root.dataset.sfReady === 'true') return;
  root.dataset.sfReady = 'true';

  class StockholmCaiaCheckout {
    constructor(el) {
      this.el = el;
      this.routesRoot = this.normalizeRoot(el.dataset.rootUrl || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/');
      this.cart = this.readInitialCart();
      this.selectedRate = null;
      this.shippingCents = 0;
      this.shippingRequestId = 0;
      this.discountCode = '';
      this.stateKey = 'sf-caia-checkout-state-v7';
      this.countryProfilesKey = 'sf-caia-checkout-country-profiles-v7';
      this.countryProfiles = this.readCountryProfiles();
      this.isSubmitting = false;
      this.shippingRequiresPostal = false;
      this.shippingRequiresProvince = false;
      this.checkoutEngine = el.dataset.checkoutEngine || 'shopify';
      this.kustomProxyUrl = (el.dataset.kustomProxyUrl || '').trim();
      this.kustomFallback = el.dataset.kustomFallback !== 'false';
      this.kustomMockMode = el.dataset.kustomMockMode === 'true' && new URLSearchParams(window.location.search).get('sf_kustom_mock') === '1';
      this.kustomOrderId = '';
      this.estimateCurrency = String(el.dataset.estimateCurrency || 'SEK').toUpperCase();
      this.shippingEstimateConfig = {
        swedenRate: this.amountToCents(el.dataset.swedenShippingRate, 79),
        swedenFreeThreshold: this.amountToCents(el.dataset.swedenFreeThreshold, 599),
        euRate: this.amountToCents(el.dataset.euShippingRate, 180),
        euFreeThreshold: this.amountToCents(el.dataset.euFreeThreshold, 1300),
        restRate: this.amountToCents(el.dataset.rowShippingRate, 299)
      };
      this.countryIsoMap = {
        aland: 'AX', alandislands: 'AX', albania: 'AL', australia: 'AU', belgium: 'BE',
        bosniaherzegovina: 'BA', bulgaria: 'BG', canada: 'CA', croatia: 'HR', cyprus: 'CY',
        egypt: 'EG', estonia: 'EE', faroeislands: 'FO', france: 'FR', greece: 'GR', hungary: 'HU',
        iceland: 'IS', ireland: 'IE', israel: 'IL', italy: 'IT', latvia: 'LV', lithuania: 'LT',
        luxembourg: 'LU', malta: 'MT', monaco: 'MC', montenegro: 'ME', poland: 'PL', portugal: 'PT',
        romania: 'RO', serbia: 'RS', slovakia: 'SK', slovenia: 'SI', spain: 'ES', sweden: 'SE',
        sverige: 'SE', switzerland: 'CH', unitedkingdom: 'GB', greatbritain: 'GB', storbritannien: 'GB',
        unitedstates: 'US', unitedstatesofamerica: 'US', usa: 'US', forentastaterna: 'US',
        unitedarabemirates: 'AE', uae: 'AE', forenadearabemiraten: 'AE'
      };
      this.phoneDialCodes = {
        AX: '+358', AL: '+355', AU: '+61', BE: '+32', BA: '+387', BG: '+359', CA: '+1',
        HR: '+385', CY: '+357', EG: '+20', EE: '+372', FO: '+298', FR: '+33', GR: '+30',
        HU: '+36', IS: '+354', IE: '+353', IL: '+972', IT: '+39', LV: '+371', LT: '+370',
        LU: '+352', MT: '+356', MC: '+377', ME: '+382', PL: '+48', PT: '+351', RO: '+40',
        RS: '+381', SK: '+421', SI: '+386', ES: '+34', SE: '+46', CH: '+41',
        GB: '+44', US: '+1', AE: '+971'
      };
      this.deliveryCountryCode = '';
      this.allowedCountries = new Set([
        'aland',
        'alandislands',
        'albania',
        'australia',
        'belgium',
        'bosniaherzegovina',
        'bulgaria',
        'canada',
        'croatia',
        'cyprus',
        'egypt',
        'estonia',
        'faroeislands',
        'france',
        'greece',
        'hungary',
        'iceland',
        'ireland',
        'israel',
        'italy',
        'latvia',
        'lithuania',
        'luxembourg',
        'malta',
        'monaco',
        'montenegro',
        'poland',
        'portugal',
        'romania',
        'serbia',
        'slovakia',
        'slovenia',
        'spain',
        'sweden',
        'sverige',
        'switzerland',
        'unitedkingdom',
        'greatbritain',
        'storbritannien',
        'unitedstates',
        'unitedstatesofamerica',
        'usa',
        'forentastaterna',
        'unitedarabemirates',
        'uae',
        'forenadearabemiraten'
      ]);
      this.euEstimateCountries = new Set([
        'belgium', 'bulgaria', 'croatia', 'cyprus', 'estonia', 'france', 'greece',
        'hungary', 'ireland', 'italy', 'latvia', 'lithuania', 'luxembourg', 'malta',
        'poland', 'portugal', 'romania', 'slovakia', 'slovenia', 'spain'
      ]);
      this.currentCountryCode = String(
        el.dataset.currentCountryCode ||
        (window.Shopify && window.Shopify.country) ||
        ''
      ).toUpperCase();
      this.activeCountries = this.readActiveCountries();
      this.activeCountryCodes = new Set(this.activeCountries.map((country) => String(country.iso_code || '').toUpperCase()).filter(Boolean));
      this.activeCountryNameToIso = new Map();
      this.activeCountries.forEach((country) => {
        const iso = String(country.iso_code || '').toUpperCase();
        const normalizedName = this.normalizeCountryName(country.name || '');
        if (iso && normalizedName) this.activeCountryNameToIso.set(normalizedName, iso);
      });
      if (document.body) document.body.classList.add('sf-checkout-page');
      this.suppressCheckoutOverlays();
      this.cache();
      this.filterAllowedCountries();
      this.buildCountryDropdown();
      this.bind();
      this.restoreState();
      this.syncCountryDropdown();
      this.renderBasket();
      this.syncOfferVisibility();
      this.loadAutomaticOffers();
      this.updateOfferNav();
    }

    suppressCheckoutOverlays() {
      if (!document.body) return;

      const hideNode = (node) => {
        if (!(node instanceof Element)) return;
        node.setAttribute('data-sf-checkout-overlay-hidden', 'true');
      };

      const hideKnown = (scope = document) => {
        const selectors = [
          '#ShopifyChat',
          'inbox-online-store-chat',
          'iframe[title*="shop with ai" i]',
          'iframe[src*="brand-agent" i]',
          'iframe[src*="brandagents" i]',
          '[id*="brand-agent" i]',
          '[id*="brandagent" i]',
          '[class*="brand-agent" i]',
          '[data-testid*="brand-agent" i]'
        ];
        selectors.forEach((selector) => {
          try {
            scope.querySelectorAll(selector).forEach(hideNode);
          } catch (error) {}
        });

        Array.from(document.body.children).forEach((node) => {
          if (!(node instanceof Element)) return;
          const meta = [
            node.id,
            node.className,
            node.getAttribute('title'),
            node.getAttribute('aria-label'),
            node.getAttribute('data-testid')
          ].map((value) => String(value || '').toLowerCase()).join(' ');

          const looksLikeBrandAgent = meta.includes('brand-agent') || meta.includes('brandagent') || meta.includes('shop with ai');
          const looksLikeClarityOverlay = meta.includes('clarity') && (() => {
            try { return window.getComputedStyle(node).position === 'fixed'; } catch (error) { return false; }
          })();
          if (looksLikeBrandAgent || looksLikeClarityOverlay) hideNode(node);

          if (node.shadowRoot) {
            try {
              const candidate = Array.from(node.shadowRoot.querySelectorAll('button, [role="button"], [aria-label], [title]')).find((entry) => {
                const label = [entry.textContent, entry.getAttribute('aria-label'), entry.getAttribute('title')]
                  .map((value) => String(value || '').toLowerCase()).join(' ');
                return label.includes('shop with ai') || label.includes('brand agent');
              });
              if (candidate) hideNode(node);
            } catch (error) {}
          }
        });
      };

      hideKnown();
      let timer = null;
      this.overlayObserver = new MutationObserver(() => {
        clearTimeout(timer);
        timer = window.setTimeout(() => hideKnown(), 60);
      });
      this.overlayObserver.observe(document.body, { childList: true, subtree: true });
    }

    normalizeRoot(value) {
      if (!value) return '/';
      return value.endsWith('/') ? value : `${value}/`;
    }

    amountToCents(value, fallback) {
      const parsed = Number(String(value == null ? '' : value).replace(',', '.'));
      const amount = Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
      return Math.round(amount * 100);
    }

    normalizeCountryName(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
    }

    isAllowedCountry(value) {
      return this.allowedCountries.has(this.normalizeCountryName(value));
    }

    readActiveCountries() {
      const node = this.el.querySelector('[data-sf-active-countries]');
      if (!node) return [];
      try {
        const payload = JSON.parse(node.textContent || '[]');
        return Array.isArray(payload) ? payload : [];
      } catch (error) {
        console.warn('[SF checkout] Active Shopify Markets could not be read.', error);
        return [];
      }
    }

    countryCodeForOption(option) {
      if (!option) return '';
      const dataCode = String((option.dataset && (option.dataset.sfCountryCode || option.dataset.countryCode)) || '').toUpperCase();
      if (dataCode) return dataCode;
      const names = [option.textContent, option.value];
      for (const name of names) {
        const normalized = this.normalizeCountryName(name);
        if (!normalized) continue;
        const activeCode = this.activeCountryNameToIso && this.activeCountryNameToIso.get(normalized);
        if (activeCode) return activeCode;
        const fallbackCode = this.countryIsoMap && this.countryIsoMap[normalized];
        if (fallbackCode) return fallbackCode;
      }
      return '';
    }

    selectedCountryCode() {
      if (!this.country || !this.country.value) return '';
      return this.countryCodeForOption(this.country.options[this.country.selectedIndex]);
    }

    phoneDialCode(countryCode) {
      return this.phoneDialCodes[String(countryCode || '').toUpperCase()] || '';
    }

    setPhoneCountryContext(countryCode) {
      const phone = this.field('phone');
      if (!phone) return;
      const dial = this.phoneDialCode(countryCode);
      phone.dataset.sfDialCode = dial || '';
      phone.placeholder = dial ? `Mobile phone (${dial})` : 'Mobile phone';
      phone.setCustomValidity('');
    }

    readCountryProfiles() {
      try {
        const payload = JSON.parse(sessionStorage.getItem(this.countryProfilesKey) || '{}');
        return payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
      } catch (error) {
        return {};
      }
    }

    persistCountryProfiles() {
      try {
        sessionStorage.setItem(this.countryProfilesKey, JSON.stringify(this.countryProfiles || {}));
      } catch (error) {}
    }

    deliveryProfileFieldNames() {
      return ['address1', 'address2', 'city', 'zip', 'phone'];
    }

    captureCountryProfile(countryCode) {
      const code = String(countryCode || '').toUpperCase();
      if (!code || !this.infoForm) return;
      const fields = {};
      this.deliveryProfileFieldNames().forEach((name) => {
        const input = this.field(name);
        fields[name] = input ? input.value : '';
      });
      this.countryProfiles[code] = {
        ts: Date.now(),
        province: this.provinceValue(),
        fields
      };
      this.persistCountryProfiles();
    }

    clearCountryProfileFields(countryCode = '') {
      if (this.infoForm) {
        this.deliveryProfileFieldNames().forEach((name) => {
          const input = this.field(name);
          if (input) {
            input.value = '';
            input.setCustomValidity('');
          }
        });
      }
      if (this.infoProvince) this.infoProvince.value = '';
      if (this.province) this.province.value = '';
      if (this.shippingZip) this.shippingZip.value = '';
      this.setPhoneCountryContext(countryCode);
    }

    restoreCountryProfile(countryCode) {
      const code = String(countryCode || '').toUpperCase();
      const profile = code ? this.countryProfiles[code] : null;
      if (!profile || !profile.fields) {
        this.clearCountryProfileFields(code);
        return false;
      }

      this.deliveryProfileFieldNames().forEach((name) => {
        const input = this.field(name);
        if (input) input.value = profile.fields[name] || '';
      });
      if (this.shippingZip) this.shippingZip.value = profile.fields.zip || '';
      if (this.infoProvince && profile.province) this.infoProvince.value = profile.province;
      if (this.province && profile.province) this.province.value = profile.province;
      this.setPhoneCountryContext(code);
      return true;
    }

    saveCurrentCountryProfile() {
      const code = this.deliveryCountryCode || this.selectedCountryCode();
      if (code) this.captureCountryProfile(code);
    }

    validatePhoneCountryMatch() {
      const phone = this.field('phone');
      if (!phone) return null;

      const countryCode = this.selectedCountryCode();
      const expectedDial = this.phoneDialCode(countryCode);
      if (!expectedDial) {
        phone.setCustomValidity('');
        return null;
      }

      let raw = String(phone.value || '').trim();
      if (!raw) {
        phone.setCustomValidity('Enter a valid mobile phone number.');
        return phone;
      }

      // Convert the common 00 international prefix to + so the comparison is consistent.
      if (raw.startsWith('00')) {
        raw = `+${raw.slice(2)}`;
        phone.value = raw;
      }

      // If the customer entered a local-format number, make it explicit before checkout.
      if (!raw.startsWith('+') && /^[0-9()\-\s]+$/.test(raw)) {
        raw = `${expectedDial} ${raw}`;
        phone.value = raw;
      }

      const compact = raw.replace(/[\s().-]/g, '');
      if (!compact.startsWith(expectedDial)) {
        const countryLabel = this.country && this.country.value ? this.country.value : 'the selected delivery country';
        phone.setCustomValidity(`Phone country code must match ${countryLabel} (${expectedDial}).`);
        return phone;
      }

      phone.dataset.sfDialCode = expectedDial;
      phone.setCustomValidity('');
      return null;
    }

    filterAllowedCountries() {
      if (!this.country) return;

      Array.from(this.country.options).forEach((option, index) => {
        if (index === 0 || !option.value) return;
        const candidate = option.textContent || option.value;
        const countryCode = this.countryCodeForOption(option);
        const allowedByCaia = this.isAllowedCountry(candidate) || this.isAllowedCountry(option.value);
        const allowedByShopify = !this.activeCountryCodes.size || (countryCode && this.activeCountryCodes.has(countryCode));
        if (!allowedByCaia || !allowedByShopify) {
          option.remove();
          return;
        }
        if (countryCode) option.dataset.sfCountryCode = countryCode;
      });

      if (this.country.value) {
        const selected = this.country.options[this.country.selectedIndex];
        const selectedCode = this.countryCodeForOption(selected);
        if (!this.isAllowedCountry(this.country.value) || (this.activeCountryCodes.size && (!selectedCode || !this.activeCountryCodes.has(selectedCode)))) {
          this.country.value = '';
        }
      }
    }


    buildCountryDropdown() {
      if (!this.country || this.countryDropdown) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'sf-country-select';
      wrapper.dataset.sfCountryCustom = '';

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'sf-country-select__trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.innerHTML = '<span data-sf-country-current></span><span class="sf-country-select__chevron" aria-hidden="true"></span>';

      const menu = document.createElement('div');
      menu.className = 'sf-country-select__menu';
      menu.hidden = true;
      menu.setAttribute('role', 'listbox');
      menu.setAttribute('aria-label', 'Delivery country');
      menu.tabIndex = -1;

      Array.from(this.country.options).forEach((option, index) => {
        if (!option.value) return;
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'sf-country-select__option';
        item.dataset.value = option.value;
        item.dataset.optionIndex = String(index);
        const countryCode = this.countryCodeForOption(option);
        if (countryCode) item.dataset.countryCode = countryCode;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.textContent = option.textContent || option.value;
        menu.appendChild(item);
      });

      this.country.parentNode.insertBefore(wrapper, this.country.nextSibling);
      wrapper.appendChild(trigger);
      wrapper.appendChild(menu);

      this.country.classList.add('sf-country-select__native');
      this.country.setAttribute('tabindex', '-1');
      this.country.setAttribute('aria-hidden', 'true');

      this.countryDropdown = wrapper;
      this.countryTrigger = trigger;
      this.countryMenu = menu;
      this.countryCurrent = trigger.querySelector('[data-sf-country-current]');

      trigger.addEventListener('click', () => this.toggleCountryDropdown());
      trigger.addEventListener('keydown', (event) => this.onCountryTriggerKeydown(event));
      menu.addEventListener('click', (event) => {
        const item = event.target.closest('.sf-country-select__option');
        if (!item) return;
        this.selectCountryFromCustom(item.dataset.value);
      });
      menu.addEventListener('keydown', (event) => this.onCountryMenuKeydown(event));

      document.addEventListener('pointerdown', (event) => {
        if (this.countryDropdown && !this.countryDropdown.contains(event.target)) this.closeCountryDropdown();
      });
    }

    toggleCountryDropdown(forceOpen) {
      if (!this.countryDropdown || !this.countryMenu || !this.countryTrigger) return;
      const willOpen = typeof forceOpen === 'boolean' ? forceOpen : this.countryMenu.hidden;
      this.countryMenu.hidden = !willOpen;
      this.countryDropdown.classList.toggle('is-open', willOpen);
      this.countryTrigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      if (willOpen) {
        const selected = this.countryMenu.querySelector('[aria-selected="true"]') || this.countryMenu.querySelector('.sf-country-select__option');
        if (selected) {
          requestAnimationFrame(() => {
            selected.scrollIntoView({ block: 'nearest' });
            selected.focus({ preventScroll: true });
          });
        }
      }
    }

    closeCountryDropdown() {
      this.toggleCountryDropdown(false);
    }

    selectCountryFromCustom(value) {
      if (!this.country) return;
      const option = Array.from(this.country.options).find((entry) => entry.value === value);
      if (!option) return;
      this.country.value = value;
      this.syncCountryDropdown();
      this.closeCountryDropdown();
      this.country.dispatchEvent(new Event('change', { bubbles: true }));
      if (this.countryTrigger) this.countryTrigger.focus({ preventScroll: true });
    }

    syncCountryDropdown() {
      if (!this.country || !this.countryCurrent) return;
      const selectedOption = this.country.options[this.country.selectedIndex];
      const placeholder = this.country.options[0] ? this.country.options[0].textContent : 'Choose your country';
      this.countryCurrent.textContent = this.country.value && selectedOption ? selectedOption.textContent : placeholder;
      if (!this.countryMenu) return;
      Array.from(this.countryMenu.querySelectorAll('.sf-country-select__option')).forEach((item) => {
        const active = item.dataset.value === this.country.value;
        item.classList.toggle('is-selected', active);
        item.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    onCountryTriggerKeydown(event) {
      if (!['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Escape'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Escape') {
        this.closeCountryDropdown();
        return;
      }
      this.toggleCountryDropdown(true);
    }

    onCountryMenuKeydown(event) {
      const options = Array.from(this.countryMenu.querySelectorAll('.sf-country-select__option'));
      if (!options.length) return;
      const currentIndex = Math.max(0, options.indexOf(document.activeElement));
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeCountryDropdown();
        this.countryTrigger.focus({ preventScroll: true });
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        const next = options[(currentIndex + delta + options.length) % options.length];
        next.focus({ preventScroll: true });
        next.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const item = document.activeElement.closest('.sf-country-select__option');
        if (item) this.selectCountryFromCustom(item.dataset.value);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        const next = event.key === 'Home' ? options[0] : options[options.length - 1];
        next.focus({ preventScroll: true });
        next.scrollIntoView({ block: 'nearest' });
      }
    }

    cache() {
      const q = (s) => this.el.querySelector(s);
      this.country = q('[data-sf-country]');
      this.provinceWrap = q('[data-sf-province-wrap]');
      this.province = q('[data-sf-province]');
      this.infoProvinceWrap = q('[data-sf-info-province-wrap]');
      this.infoProvince = q('[data-sf-info-province]');
      this.shippingZipWrap = q('[data-sf-shipping-zip-wrap]');
      this.shippingZip = q('[data-sf-shipping-zip]');
      this.shipping = q('[data-sf-shipping]');
      this.shippingStatus = q('[data-sf-shipping-status]');
      this.shippingRates = q('[data-sf-shipping-rates]');
      this.continueButton = q('[data-sf-continue]');
      this.info = q('[data-sf-info]');
      this.infoForm = q('[data-sf-info-form]');
      this.countryNote = q('[data-sf-country-note]');
      this.editDeliveryButton = q('[data-sf-edit-delivery]');
      this.marketing = q('[data-sf-marketing]');
      this.formMessage = q('[data-sf-form-message]');
      this.finalTotal = q('[data-sf-final-total]');
      this.basket = q('[data-sf-basket]');
      this.basketInner = q('[data-sf-basket-inner]');
      this.basketItems = q('[data-sf-basket-items]');
      this.basketCount = q('[data-sf-basket-count]');
      this.mobileBasketTotal = q('[data-sf-basket-mobile-total]');
      this.productsTotal = q('[data-sf-products-total]');
      this.discountRow = q('[data-sf-discount-row]');
      this.discountTotal = q('[data-sf-discount-total]');
      this.shippingTotal = q('[data-sf-shipping-total]');
      this.toPay = q('[data-sf-to-pay]');
      this.discountInput = q('[data-sf-discount-code]');
      this.discountApply = q('[data-sf-discount-apply]');
      this.discountMessage = q('[data-sf-discount-message]');
      this.freeShippingNotice = q('.sf-basket__free-shipping shipping-notice[data-free-shipping]');
      this.offerTrack = q('[data-sf-offers-track]');
      this.offerViewport = q('[data-sf-offers-viewport]');
      this.offerPrev = q('[data-sf-offers-prev]');
      this.offerNext = q('[data-sf-offers-next]');
      this.offerMessage = q('[data-sf-offer-message]');
      this.basketToggle = q('[data-sf-basket-toggle]');
      this.kustom = q('[data-sf-kustom]');
      this.kustomStatus = q('[data-sf-kustom-status]');
      this.kustomMount = q('[data-sf-kustom-mount]');
      this.kustomBack = q('[data-sf-kustom-back]');
      this.localizationForm =
        document.getElementById('SFCheckoutLocalizationForm') ||
        document.querySelector('form[action*="/localization"]');

      this.localizationCountryInput =
        document.getElementById('SFCheckoutLocalizationCountry') ||
        (this.localizationForm
          ? this.localizationForm.querySelector('[name="country_code"]')
          : null);

      this.localizationReturnInput =
        document.getElementById('SFCheckoutLocalizationReturn') ||
        (this.localizationForm
          ? this.localizationForm.querySelector('[name="return_to"]')
          : null);
    }

    bind() {
      if (this.country) {
        this.country.addEventListener('change', () => this.onCountryChange());
      }

      if (this.province) {
        this.province.addEventListener('change', () => {
          this.shippingRequiresProvince = true;
          if (this.infoProvince && this.province.value) this.infoProvince.value = this.province.value;
          this.saveState();
          if (this.province.value) this.calculateShipping();
        });
      }

      if (this.infoProvince) {
        this.infoProvince.addEventListener('change', () => {
          if (this.province) this.province.value = this.infoProvince.value || '';
          this.saveCurrentCountryProfile();
          this.saveState();
        });
      }

      if (this.shippingZip) {
        let timer;
        this.shippingZip.addEventListener('input', () => {
          this.shippingRequiresPostal = true;
          this.saveState();
          clearTimeout(timer);
          timer = setTimeout(() => {
            if (this.shippingZip.value.trim().length >= 2) this.calculateShipping();
          }, 450);
        });
      }

      if (this.continueButton) {
        this.continueButton.addEventListener('click', () => this.revealInformation());
      }

      if (this.editDeliveryButton) {
        this.editDeliveryButton.addEventListener('click', () => this.editDelivery());
      }

      if (this.infoForm) {
        let infoZipTimer;
        this.infoForm.addEventListener('input', (event) => {
          this.saveCurrentCountryProfile();
          this.saveState();
          if (event.target && event.target.dataset && event.target.dataset.sfField === 'zip' && this.country && this.country.value) {
            if (this.shippingZip) this.shippingZip.value = event.target.value;
            if (this.shippingRequiresPostal) {
              clearTimeout(infoZipTimer);
              infoZipTimer = setTimeout(() => this.calculateShipping(), 450);
            }
          }
        });
        this.infoForm.addEventListener('submit', (event) => {
          event.preventDefault();
          this.goToSecureCheckout();
        });
      }

      if (this.discountApply) {
        this.discountApply.addEventListener('click', () => this.applyDiscount());
      }

      if (this.discountInput) {
        this.discountInput.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.applyDiscount();
          }
        });
      }

      if (this.offerPrev) {
        this.offerPrev.addEventListener('click', () => this.scrollOffers(-1));
      }

      if (this.offerNext) {
        this.offerNext.addEventListener('click', () => this.scrollOffers(1));
      }

      if (this.offerTrack) {
        this.offerTrack.addEventListener('scroll', () => this.updateOfferNav(), { passive: true });
        this.offerTrack.addEventListener('click', (event) => {
          const button = event.target.closest('[data-sf-offer-add]');
          if (!button) return;
          this.addOffer(button);
        });
      }

      if (this.basketItems) {
        this.basketItems.addEventListener('click', (event) => {
          const item = event.target.closest('[data-sf-basket-item]');
          if (!item) return;
          const key = item.dataset.key;
          const line = this.cart.items.find((entry) => entry.key === key);
          if (!line) return;

          if (event.target.closest('[data-sf-qty-minus]')) {
            this.changeLine(key, Math.max(0, line.quantity - 1));
          } else if (event.target.closest('[data-sf-qty-plus]')) {
            this.changeLine(key, line.quantity + 1);
          } else if (event.target.closest('[data-sf-remove]')) {
            this.changeLine(key, 0);
          }
        });
      }

      if (this.shippingRates) {
        this.shippingRates.addEventListener('change', (event) => {
          const input = event.target.closest('input[type="radio"][data-sf-rate-index]');
          if (!input) return;
          const rates = this.currentRates || [];
          this.selectRate(rates[Number(input.dataset.sfRateIndex)]);
        });
      }

      if (this.basketToggle) {
        this.basketToggle.addEventListener('click', () => {
          const open = !this.basket.classList.contains('is-open');
          this.basket.classList.toggle('is-open', open);
          this.basketToggle.setAttribute('aria-expanded', String(open));
        });
      }

      if (this.kustomBack) {
        this.kustomBack.addEventListener('click', () => this.resetKustomCheckout());
      }
    }

    readInitialCart() {
      const node = this.el.querySelector('[data-sf-initial-cart]');
      if (!node) return { items: [], item_count: 0, total_price: 0, items_subtotal_price: 0, total_discount: 0, currency: this.el.dataset.currency };
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.warn('[SF checkout] Initial cart JSON could not be read.', error);
        return { items: [], item_count: 0, total_price: 0, items_subtotal_price: 0, total_discount: 0, currency: this.el.dataset.currency };
      }
    }

    money(cents) {
      const currency = (this.cart && this.cart.currency) || this.el.dataset.currency || 'SEK';
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency,
          currencyDisplay: 'symbol'
        }).format(amount);
      } catch (error) {
        return `${amount.toFixed(2)} ${currency}`;
      }
    }

    escape(value) {
      const div = document.createElement('div');
      div.textContent = value == null ? '' : String(value);
      return div.innerHTML;
    }

    async refreshCart() {
      const response = await fetch(`${this.routesRoot}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Unable to refresh cart.');
      this.cart = await response.json();
      this.el.dataset.requiresShipping = String(Boolean(this.cart.requires_shipping));
      this.renderBasket();
      this.syncOfferVisibility();
      document.querySelectorAll('[data-header-cart-count]').forEach((node) => {
        node.textContent = this.cart.item_count;
      });
      document.querySelectorAll('[data-header-cart-total]').forEach((node) => {
        node.textContent = this.money(this.cart.total_price);
      });
      return this.cart;
    }

    renderBasket() {
      if (!this.basketItems || !this.cart) return;

      this.basketItems.innerHTML = (this.cart.items || []).map((item) => {
        const image = item.featured_image && item.featured_image.url ? item.featured_image.url : item.image;
        const variant = item.variant_title && item.variant_title !== 'Default Title'
          ? `<span class="sf-basket-item__variant">${this.escape(item.variant_title)}</span>`
          : '';
        return `
          <article class="sf-basket-item" data-sf-basket-item data-key="${this.escape(item.key)}">
            <a class="sf-basket-item__image" href="${this.escape(item.url || '#')}">
              ${image ? `<img src="${this.escape(image)}" alt="${this.escape(item.product_title || item.title || '')}" loading="eager">` : ''}
            </a>
            <div class="sf-basket-item__content">
              <a class="sf-basket-item__title" href="${this.escape(item.url || '#')}">${this.escape(item.product_title || item.title || '')}</a>
              ${variant}
              <div class="sf-basket-item__quantity">
                <button type="button" data-sf-qty-minus aria-label="Decrease quantity">−</button>
                <span data-sf-qty>${Number(item.quantity || 0)}</span>
                <button type="button" data-sf-qty-plus aria-label="Increase quantity">+</button>
              </div>
            </div>
            <div class="sf-basket-item__side">
              <button type="button" class="sf-basket-item__remove" data-sf-remove aria-label="Remove ${this.escape(item.product_title || item.title || '')}">×</button>
              <strong>${this.money(item.final_line_price != null ? item.final_line_price : item.line_price)}</strong>
            </div>
          </article>
        `;
      }).join('');

      if (this.basketCount) this.basketCount.textContent = this.cart.item_count || 0;
      if (this.productsTotal) this.productsTotal.textContent = this.money(this.cart.items_subtotal_price != null ? this.cart.items_subtotal_price : this.cart.total_price);

      const discount = Number(this.cart.total_discount || 0);
      if (this.discountRow) this.discountRow.hidden = discount <= 0;
      if (this.discountTotal) this.discountTotal.textContent = `-${this.money(discount)}`;

      this.updateTotals();
      this.syncFreeShippingNotice();

      if (this.cart.item_count === 0) {
        window.location.assign(this.routesRoot + 'cart');
      }
    }

    updateTotals() {
      const shippingPending = Boolean(this.selectedRate && this.selectedRate.sf_price_pending);
      const shipping = Number(this.shippingCents || 0);
      const base = Number((this.cart && this.cart.total_price) || 0);
      const total = base + shipping;
      const totalLabel = shippingPending ? `${this.money(base)} + shipping` : this.money(total);
      if (this.shippingTotal) {
        this.shippingTotal.textContent = this.selectedRate
          ? (shippingPending ? 'Calculated at payment' : (shipping === 0 ? 'FREE' : this.money(shipping)))
          : '—';
      }
      if (this.toPay) this.toPay.textContent = totalLabel;
      if (this.finalTotal) this.finalTotal.textContent = totalLabel;
      if (this.mobileBasketTotal) this.mobileBasketTotal.textContent = shippingPending ? this.money(base) : this.money(total);
    }

    async changeLine(key, quantity) {
      this.basket.classList.add('is-processing');
      this.basket.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${this.routesRoot}cart/change.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ id: key, quantity })
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to update cart.');
        this.cart = payload;
        this.resetShippingAfterCartChange();
        this.renderBasket();
        this.syncOfferVisibility();
      } catch (error) {
        alert(error.message || 'Unable to update cart.');
      } finally {
        this.basket.classList.remove('is-processing');
        this.basket.removeAttribute('aria-busy');
      }
    }

    resetShippingAfterCartChange() {
      this.selectedRate = null;
      this.shippingCents = 0;
      this.currentRates = [];
      if (this.shippingRates) this.shippingRates.innerHTML = '';
      if (this.continueButton) this.continueButton.disabled = true;
      this.updateTotals();
      this.syncFreeShippingNotice();

      // Re-fetch Shopify's live rate after quantity/cart changes so the visible
      // carrier, shipping price and free-shipping state stay in sync.
      if (this.country && this.country.value) this.calculateShipping();
    }

    currencyRate() {
      const rate = window.Shopify && Shopify.currency && Number(Shopify.currency.rate);
      return Number.isFinite(rate) && rate > 0 ? rate : 1;
    }

    toPresentmentCents(baseCurrencyCents) {
      return Math.round(Number(baseCurrencyCents || 0) * this.currencyRate());
    }

    freeShippingThresholdBaseForCountry() {
      const country = this.normalizeCountryName(this.country ? this.country.value : '');
      if (!country) return 0;
      if (country === 'sweden' || country === 'sverige') return this.shippingEstimateConfig.swedenFreeThreshold;
      if (this.euEstimateCountries.has(country)) return this.shippingEstimateConfig.euFreeThreshold;
      return 0;
    }

    syncFreeShippingNotice() {
      const notice = this.freeShippingNotice || this.el.querySelector('.sf-basket__free-shipping shipping-notice[data-free-shipping]');
      if (!notice || !this.cart) return;

      const wrapper = notice.closest('.sf-basket__free-shipping');
      const thresholdBase = this.freeShippingThresholdBaseForCountry();

      // Use the real country-specific Shopify threshold instead of the theme's
      // single global free-shipping setting. Hide the notice for destinations
      // that do not have a configured free-shipping threshold.
      if (!thresholdBase) {
        if (wrapper) wrapper.hidden = true;
        return;
      }
      if (wrapper) wrapper.hidden = false;

      notice.setAttribute('data-free-shipping', String(thresholdBase));
      notice.setAttribute('data-cart-total', String(Number(this.cart.total_price || 0)));

      // The theme component formats the localized amount and uses Shopify's
      // presentment-currency rate once supplied the correct base threshold.
      if (typeof notice.init === 'function') {
        notice.init();
        return;
      }

      const threshold = this.toPresentmentCents(thresholdBase);
      if (!threshold) return;

      const total = Number(this.cart.total_price || 0);
      const remaining = threshold - total;
      const text = notice.querySelector('[data-js-free-shipping-text]');
      const slider = notice.querySelector('[data-js-free-shipping-slider]');
      if (text) {
        text.textContent = remaining > 0
          ? `You are ${this.money(remaining)} away from free shipping.`
          : 'You are eligible for free shipping!';
      }
      if (slider) slider.style.width = `${Math.max(0, Math.min(100, total * 100 / threshold))}%`;
    }

    async addOffer(button) {
      const variantId = Number(button.dataset.variantId);
      if (!variantId) return;
      const previous = button.textContent;
      button.disabled = true;
      button.classList.add('is-loading');
      button.textContent = 'Adding…';
      try {
        const response = await fetch(`${this.routesRoot}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to add product.');
        await this.refreshCart();
        this.resetShippingAfterCartChange();
        this.setMessage(this.offerMessage, 'Added to your basket.');
      } catch (error) {
        this.setMessage(this.offerMessage, error.message || 'Unable to add product.', true);
      } finally {
        button.classList.remove('is-loading');
        button.textContent = previous;
        button.disabled = false;
      }
    }

    syncOfferVisibility() {
      if (!this.offerTrack || !this.cart) return;
      const cartProductIds = new Set((this.cart.items || []).map((item) => String(item.product_id)));
      this.offerTrack.querySelectorAll('[data-sf-offer-card]').forEach((card) => {
        card.hidden = cartProductIds.has(String(card.dataset.productId));
      });
      this.updateOfferNav();
    }

    async loadAutomaticOffers() {
      if (this.el.dataset.offerSource !== 'automatic' || !this.offerTrack || !this.cart || !this.cart.items || !this.cart.items.length) return;
      const firstProductId = this.cart.items[0].product_id;
      const limit = Math.max(2, Math.min(8, Number(this.el.dataset.offerLimit || 6)));
      const intent = this.el.dataset.recommendationIntent === 'complementary' ? 'complementary' : 'related';

      try {
        const response = await fetch(`${this.routesRoot}recommendations/products.json?product_id=${encodeURIComponent(firstProductId)}&limit=${limit}&intent=${intent}`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return;
        const payload = await response.json();
        const products = payload.products || [];
        const existingIds = new Set(Array.from(this.offerTrack.querySelectorAll('[data-sf-offer-card]')).map((card) => String(card.dataset.productId)));
        const cartIds = new Set((this.cart.items || []).map((item) => String(item.product_id)));

        products.forEach((product) => {
          if (existingIds.has(String(product.id)) || cartIds.has(String(product.id))) return;
          const variant = (product.variants || []).find((entry) => entry.available) || (product.variants || [])[0];
          if (!variant) return;
          const card = document.createElement('article');
          card.className = 'sf-offer-card';
          card.dataset.sfOfferCard = '';
          card.dataset.productId = product.id;
          const image = product.featured_image || (product.images && product.images[0]) || '';
          const compare = variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price)
            ? `<del>${this.money(variant.compare_at_price)}</del>`
            : '';
          card.innerHTML = `
            <a class="sf-offer-card__media" href="${this.escape(product.url || `${this.routesRoot}products/${product.handle}`)}">
              ${image ? `<img src="${this.escape(typeof image === 'string' ? image : image.src || image.url || '')}" alt="${this.escape(product.title || '')}" loading="eager" decoding="async">` : ''}
            </a>
            <div class="sf-offer-card__content">
              <a class="sf-offer-card__title" href="${this.escape(product.url || `${this.routesRoot}products/${product.handle}`)}">${this.escape(product.title || '')}</a>
              ${product.type ? `<span class="sf-offer-card__caption">${this.escape(product.type)}</span>` : ''}
              <div class="sf-offer-card__prices">
                <strong>${this.money(variant.price)}</strong>${compare}
              </div>
              <button type="button" class="sf-offer-card__add" data-sf-offer-add data-variant-id="${this.escape(variant.id)}">Add</button>
            </div>
          `;
          this.offerTrack.appendChild(card);
          existingIds.add(String(product.id));
        });
        this.syncOfferVisibility();
      } catch (error) {
        console.warn('[SF checkout] Automatic offers could not be loaded.', error);
      }
    }

    scrollOffers(direction) {
      if (!this.offerTrack) return;
      const card = this.offerTrack.querySelector('[data-sf-offer-card]:not([hidden])');
      const amount = card ? card.getBoundingClientRect().width + 14 : this.offerTrack.clientWidth * 0.8;
      this.offerTrack.scrollBy({ left: direction * amount, behavior: 'smooth' });
    }

    updateOfferNav() {
      if (!this.offerTrack) return;
      const max = this.offerTrack.scrollWidth - this.offerTrack.clientWidth;
      if (this.offerPrev) this.offerPrev.disabled = this.offerTrack.scrollLeft <= 4;
      if (this.offerNext) this.offerNext.disabled = max <= 4 || this.offerTrack.scrollLeft >= max - 4;
    }

    onCountryChange() {
      const previousCountryCode = this.deliveryCountryCode || '';
      const selectedCountryCode = this.selectedCountryCode();

      // Preserve delivery details against the country they belong to before changing context.
      if (previousCountryCode && previousCountryCode !== selectedCountryCode) {
        this.captureCountryProfile(previousCountryCode);
      }

      this.syncCountryDropdown();
      this.selectedRate = null;
      this.shippingCents = 0;
      this.currentRates = [];
      this.shippingRequiresPostal = false;
      this.shippingRequiresProvince = false;
      this.setupProvinceOptions();
      this.provinceWrap.hidden = true;
      this.shippingZipWrap.hidden = true;
      this.setInformationMode(false);
      this.continueButton.disabled = true;
      this.updateTotals();

      if (!this.country.value) {
        this.deliveryCountryCode = '';
        this.clearCountryProfileFields('');
        this.shipping.hidden = true;
        this.saveState();
        return;
      }

      if (!this.isAllowedCountry(this.country.value)) {
        this.country.value = '';
        this.deliveryCountryCode = '';
        this.syncCountryDropdown();
        this.clearCountryProfileFields('');
        this.shipping.hidden = true;
        this.setMessage(this.formMessage, 'This delivery country is not available.', true);
        this.saveState();
        return;
      }

      this.deliveryCountryCode = selectedCountryCode;
      // First visit to a country starts with a blank delivery address/phone.
      // Returning to a previously used country restores that country's details.
      this.restoreCountryProfile(selectedCountryCode);
      this.syncFreeShippingNotice();
      this.saveState();

      if (this.activeCountryCodes.size && (!selectedCountryCode || !this.activeCountryCodes.has(selectedCountryCode))) {
        this.country.value = '';
        this.deliveryCountryCode = '';
        this.syncCountryDropdown();
        this.clearCountryProfileFields('');
        this.shipping.hidden = true;
        this.setMessage(this.formMessage, 'This delivery country is not enabled in Shopify Markets.', true);
        this.saveState();
        return;
      }

      const storefrontCountryCode = String(
        (window.Shopify && window.Shopify.country) ||
        this.currentCountryCode ||
        ''
      ).toUpperCase();

      if (selectedCountryCode && selectedCountryCode !== storefrontCountryCode) {
        if (this.switchShopifyMarket(selectedCountryCode)) return;
      }

      this.shipping.hidden = false;
      if (this.cart && this.cart.requires_shipping === false) {
        this.renderNoShippingRequired();
        return;
      }

      // Try Shopify's live shipping-rate endpoint immediately so the real
      // shipping method / carrier can be shown when Shopify can resolve it
      // from the destination country alone. If Shopify requires a postal code
      // or province, calculateShipping() safely falls back to the provisional
      // country-level estimate until the customer enters the full address.
      this.calculateShipping();
    }

    switchShopifyMarket(countryCode) {
      if (!countryCode) return false;

      const code = String(countryCode).toUpperCase();
      const form =
        this.localizationForm ||
        document.querySelector('form[action*="/localization"]');

      if (!form) {
        console.warn('[SF checkout] Shopify localization form was not found.');
        return false;
      }

      this.saveState();
      this.el.classList.add('is-market-switching');

      if (this.shipping) this.shipping.hidden = false;
      if (this.shippingStatus) this.shippingStatus.textContent = 'Updating prices and delivery options…';
      if (this.shippingRates) this.shippingRates.innerHTML = '';
      if (this.continueButton) this.continueButton.disabled = true;

      let countryInput =
        this.localizationCountryInput ||
        form.querySelector('[name="country_code"]');

      if (!countryInput) {
        countryInput = document.createElement('input');
        countryInput.type = 'hidden';
        countryInput.name = 'country_code';
        form.appendChild(countryInput);
      }

      countryInput.value = code;

      let returnInput =
        this.localizationReturnInput ||
        form.querySelector('[name="return_to"]');

      if (!returnInput) {
        returnInput = document.createElement('input');
        returnInput.type = 'hidden';
        returnInput.name = 'return_to';
        form.appendChild(returnInput);
      }

      const query = window.location.search || '';
      returnInput.value = `${window.location.pathname}${query}#sf-checkout-flow`;

      this.localizationForm = form;
      this.localizationCountryInput = countryInput;
      this.localizationReturnInput = returnInput;
      this.currentCountryCode = code;

      window.setTimeout(() => {
        form.submit();
      }, 50);

      return true;
    }

    setupProvinceOptions() {
      if (!this.country || !this.province || !this.provinceWrap) return;
      const option = this.country.options[this.country.selectedIndex];
      let provinces = [];
      try {
        provinces = JSON.parse(option && option.dataset && option.dataset.provinces ? option.dataset.provinces : '[]');
      } catch (error) {
        provinces = [];
      }

      const populate = (select, placeholder) => {
        if (!select) return;
        select.innerHTML = '';
        const empty = document.createElement('option');
        empty.value = '';
        empty.textContent = placeholder;
        select.appendChild(empty);
        provinces.forEach((province) => {
          const entry = document.createElement('option');
          entry.value = province[0];
          entry.textContent = province[1];
          select.appendChild(entry);
        });
      };

      populate(this.province, 'Choose province / state');
      populate(this.infoProvince, 'Province / State');
      this.provinceWrap.hidden = true;
      if (this.infoProvinceWrap) this.infoProvinceWrap.hidden = !provinces.length;
    }

    estimateRateForCountry() {
      const country = this.normalizeCountryName(this.country ? this.country.value : '');
      const subtotal = Number(this.cart && this.cart.total_price ? this.cart.total_price : 0);
      const currentCurrency = String((this.cart && this.cart.currency) || this.el.dataset.currency || 'SEK').toUpperCase();
      let cents = this.shippingEstimateConfig.restRate;
      let freeThresholdBase = 0;
      let name = 'Standard delivery';
      let code = 'sf-estimate-rest';

      // Free-shipping thresholds are stored in the shop's base currency. Convert
      // the threshold to the active presentment currency before comparing it with
      // the cart total so EU/foreign-market eligibility matches Shopify.
      if (country === 'sweden' || country === 'sverige') {
        cents = this.shippingEstimateConfig.swedenRate;
        freeThresholdBase = this.shippingEstimateConfig.swedenFreeThreshold;
        code = 'sf-estimate-se';
      } else if (this.euEstimateCountries.has(country)) {
        cents = this.shippingEstimateConfig.euRate;
        freeThresholdBase = this.shippingEstimateConfig.euFreeThreshold;
        code = 'sf-estimate-eu';
      }

      const freeThresholdPresentment = freeThresholdBase ? this.toPresentmentCents(freeThresholdBase) : 0;
      if (freeThresholdPresentment && subtotal >= freeThresholdPresentment) cents = 0;

      // Paid fixed rates stay pending in foreign currencies until secure checkout
      // confirms the localized amount. FREE is safe to show in every currency.
      const pricePending = currentCurrency !== this.estimateCurrency && cents !== 0;
      return {
        name,
        presentment_name: name,
        code,
        source: 'shopify',
        price: pricePending ? null : (cents / 100).toFixed(2),
        sf_price_pending: pricePending,
        sf_provisional: true
      };
    }

    renderCountryShippingEstimate() {
      if (!this.shipping || !this.country || !this.country.value) return;
      this.shipping.hidden = false;
      this.shippingRequiresPostal = false;
      this.shippingRequiresProvince = false;
      if (this.provinceWrap) this.provinceWrap.hidden = true;
      if (this.shippingZipWrap) this.shippingZipWrap.hidden = true;
      if (this.shippingStatus) this.shippingStatus.textContent = '';
      this.renderShippingRates([this.estimateRateForCountry()]);
    }

    fullAddressShippingParams() {
      const params = new URLSearchParams();
      params.set('shipping_address[country]', this.country ? this.country.value : '');
      const zipField = this.field('zip');
      const zip = zipField ? zipField.value.trim() : '';
      if (zip) params.set('shipping_address[zip]', zip);
      const province = this.provinceValue();
      if (province) params.set('shipping_address[province]', province);
      return params;
    }

    async confirmShippingBeforePayment() {
      if (!this.cart || this.cart.requires_shipping === false) return true;
      const params = this.fullAddressShippingParams();
      const query = params.toString();
      if (!query) return false;

      try {
        const payload = await this.legacyShippingRates(query);
        const rates = payload && Array.isArray(payload.shipping_rates) ? payload.shipping_rates : [];
        if (!rates.length) return false;
        this.renderShippingRates(rates);
        return true;
      } catch (error) {
        console.warn('[SF checkout] Final shipping confirmation will be handled by secure checkout.', error);
        return false;
      }
    }

    shippingParams() {
      const params = new URLSearchParams();
      params.set('shipping_address[country]', this.country ? this.country.value : '');
      if (this.shippingRequiresProvince && this.province && this.province.value) {
        params.set('shipping_address[province]', this.province.value);
      }
      if (this.shippingRequiresPostal && this.shippingZip && this.shippingZip.value.trim()) {
        params.set('shipping_address[zip]', this.shippingZip.value.trim());
      }
      return params;
    }

    async calculateShipping() {
      if (!this.country || !this.country.value || !this.shipping) return;
      const requestId = ++this.shippingRequestId;
      this.shipping.hidden = false;
      this.shippingStatus.textContent = 'Loading delivery options…';
      this.shippingRates.innerHTML = '';
      this.continueButton.disabled = true;
      this.selectedRate = null;
      this.shippingCents = 0;
      this.updateTotals();

      const params = this.shippingParams();
      const query = params.toString();

      try {
        const prepare = await fetch(`${this.routesRoot}cart/prepare_shipping_rates.json?${query}`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });

        if (!prepare.ok) {
          const problem = await this.safeJson(prepare);
          throw this.shippingError(problem, prepare.status);
        }

        let payload = null;
        for (let attempt = 0; attempt < 10; attempt += 1) {
          if (requestId !== this.shippingRequestId) return;
          await this.delay(attempt < 2 ? 350 : 550);
          const response = await fetch(`${this.routesRoot}cart/async_shipping_rates.json?${query}`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
          });
          if (!response.ok) {
            const problem = await this.safeJson(response);
            throw this.shippingError(problem, response.status);
          }
          payload = await response.json();
          if (payload && payload.shipping_rates) break;
        }

        if (!payload || !payload.shipping_rates) {
          payload = await this.legacyShippingRates(query);
        }

        if (requestId !== this.shippingRequestId) return;
        this.renderShippingRates(payload && payload.shipping_rates ? payload.shipping_rates : []);
      } catch (error) {
        if (requestId !== this.shippingRequestId) return;
        if (error && (error.requiresProvince || error.requiresPostal)) {
          // Do not interrupt the CAIA-style country-first step. The full address
          // is collected after Continue and the authoritative rate is confirmed
          // before Kustom or by Shopify's secure checkout.
          this.renderCountryShippingEstimate();
          this.saveState();
          return;
        }

        try {
          const legacy = await this.legacyShippingRates(query);
          if (requestId !== this.shippingRequestId) return;
          this.renderShippingRates(legacy && legacy.shipping_rates ? legacy.shipping_rates : []);
        } catch (legacyError) {
          this.shippingStatus.textContent = legacyError.message || error.message || 'Delivery options could not be calculated.';
        }
      }
    }

    async legacyShippingRates(query) {
      const response = await fetch(`${this.routesRoot}cart/shipping_rates.json?${query}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      const payload = await this.safeJson(response);
      if (!response.ok) throw this.shippingError(payload, response.status);
      return payload;
    }

    shippingError(payload, status) {
      const raw = JSON.stringify(payload || {}).toLowerCase();
      const error = new Error(this.flattenErrors(payload) || `Shipping request failed (${status}).`);
      const hasPostal = Boolean(this.shippingZip && this.shippingZip.value.trim());
      const hasProvince = Boolean(this.province && this.province.value);
      error.requiresPostal = !hasPostal && (raw.includes('zip') || raw.includes('postal'));
      error.requiresProvince = !hasProvince && (raw.includes('province') || raw.includes('state'));
      return error;
    }

    flattenErrors(payload) {
      if (!payload) return '';
      if (typeof payload === 'string') return payload;
      if (payload.description) return payload.description;
      const parts = [];
      const walk = (value) => {
        if (Array.isArray(value)) value.forEach(walk);
        else if (value && typeof value === 'object') Object.values(value).forEach(walk);
        else if (value != null) parts.push(String(value));
      };
      walk(payload);
      return parts.join(' ');
    }

    safeJson(response) {
      return response.json().catch(() => null);
    }

    renderShippingRates(rates) {
      const previousRateKey = this.rateKey(this.selectedRate);
      this.currentRates = rates || [];
      this.shippingRates.innerHTML = '';

      if (!this.currentRates.length) {
        this.renderCountryShippingEstimate();
        return;
      }

      this.shippingStatus.textContent = '';
      this.currentRates.forEach((rate, index) => {
        const label = document.createElement('label');
        label.className = 'sf-shipping-rate';
        if (rate.sf_provisional) label.classList.add('is-estimate');
        const pending = Boolean(rate.sf_price_pending);
        const cents = pending ? null : this.rateToCents(rate);
        const carrier = this.carrierLabel(rate);
        const delivery = this.deliveryText(rate);
        const rateName = rate.presentment_name || rate.name || rate.code || 'Delivery';
        const priceLabel = pending ? 'Calculated at payment' : (cents === 0 ? 'FREE' : this.money(cents));
        label.innerHTML = `
          <input type="radio" name="sf_shipping_rate" value="${index}" data-sf-rate-index="${index}" ${index === 0 ? 'checked' : ''}>
          <span class="sf-shipping-rate__content">
            <span class="sf-shipping-rate__name">${this.escape(rateName)}</span>
            ${delivery ? `<small class="sf-shipping-rate__delivery">${this.escape(delivery)}</small>` : ''}
            ${rate.sf_provisional ? '<span class="sf-shipping-rate__estimate">Estimated</span>' : ''}
          </span>
          <span class="sf-shipping-rate__price${pending ? ' is-pending' : ''}">${this.escape(priceLabel)}</span>
          ${carrier ? `<span class="sf-shipping-rate__carrier" data-carrier="${this.escape(carrier.toLowerCase())}">${this.escape(carrier)}</span>` : ''}
        `;
        this.shippingRates.appendChild(label);
      });

      const selectedIndex = previousRateKey
        ? Math.max(0, this.currentRates.findIndex((rate) => this.rateKey(rate) === previousRateKey))
        : 0;
      const radios = this.shippingRates.querySelectorAll('input[type="radio"][data-sf-rate-index]');
      radios.forEach((radio, index) => { radio.checked = index === selectedIndex; });
      this.selectRate(this.currentRates[selectedIndex] || this.currentRates[0]);
    }

    rateKey(rate) {
      if (!rate) return '';
      return [rate.code || '', rate.presentment_name || rate.name || '', rate.source || '']
        .map((part) => String(part).trim().toLowerCase())
        .join('|');
    }

    renderNoShippingRequired() {
      this.currentRates = [];
      this.selectedRate = { name: 'No shipping required', price: '0.00' };
      this.shippingCents = 0;
      this.shippingStatus.textContent = '';
      this.shippingRates.innerHTML = '<div class="sf-shipping-rate sf-shipping-rate--static"><span></span><span class="sf-shipping-rate__content"><span class="sf-shipping-rate__name">No shipping required</span></span><span class="sf-shipping-rate__price">FREE</span></div>';
      this.continueButton.disabled = false;
      this.updateTotals();
    }

    selectRate(rate) {
      if (!rate) return;
      this.selectedRate = rate;
      this.shippingCents = rate.sf_price_pending ? 0 : this.rateToCents(rate);
      this.continueButton.disabled = false;
      this.updateTotals();
      this.saveState();
    }

    rateToCents(rate) {
      if (!rate || rate.sf_price_pending || rate.price == null || rate.price === '') return 0;
      const raw = Number(rate.price);
      return Number.isFinite(raw) ? Math.round(raw * 100) : 0;
    }

    carrierLabel(rate) {
      const name = `${rate.presentment_name || ''} ${rate.name || ''} ${rate.source || ''}`.toLowerCase();
      if (name.includes('dhl')) return 'DHL';
      if (name.includes('postnord')) return 'POSTNORD';
      if (name.includes('bring')) return 'BRING';
      if (name.includes('budbee')) return 'BUDBEE';
      if (name.includes('instabox')) return 'INSTABOX';
      if (name.includes('fedex')) return 'FEDEX';
      if (name.includes('ups')) return 'UPS';
      if (name.includes('dpd')) return 'DPD';
      if (name.includes('gls')) return 'GLS';
      if (name.includes('schenker')) return 'DB SCHENKER';
      if (name.includes('royal mail')) return 'ROYAL MAIL';
      if (name.includes('evri')) return 'EVRI';
      if (name.includes('postnl')) return 'POSTNL';
      if (name.includes('colissimo')) return 'COLISSIMO';
      if (name.includes('chronopost')) return 'CHRONOPOST';
      return rate.source && rate.source !== 'shopify' ? String(rate.source).toUpperCase().slice(0, 18) : '';
    }

    deliveryText(rate) {
      if (Array.isArray(rate.delivery_range) && rate.delivery_range.length === 2) {
        return `${rate.delivery_range[0]} – ${rate.delivery_range[1]}`;
      }
      if (rate.delivery_date) return `Estimated ${rate.delivery_date}`;
      return '';
    }

    setInformationMode(open) {
      if (this.info) this.info.hidden = !open;
      if (this.continueButton) this.continueButton.hidden = Boolean(open);
      this.el.classList.toggle('is-information-open', Boolean(open));
    }

    revealInformation() {
      if (!this.country.value || (!this.selectedRate && this.cart.requires_shipping !== false)) return;
      this.deliveryCountryCode = this.selectedCountryCode();
      this.setPhoneCountryContext(this.deliveryCountryCode);
      this.setInformationMode(true);
      if (this.shippingZip.value && !this.field('zip').value) this.field('zip').value = this.shippingZip.value;
      if (this.infoProvince && this.province && this.province.value) this.infoProvince.value = this.province.value;
      this.countryNote.textContent = `Delivery to ${this.country.value}`;
      if (this.basket && window.matchMedia('(max-width: 960px)').matches) {
        this.basket.classList.remove('is-open');
        if (this.basketToggle) this.basketToggle.setAttribute('aria-expanded', 'false');
      }
      this.saveState();
      requestAnimationFrame(() => {
        this.info.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const email = this.field('email');
        if (email && !email.value) email.focus({ preventScroll: true });
      });
    }

    editDelivery() {
      if (!this.info) return;
      if (this.kustom && !this.kustom.hidden) this.resetKustomCheckout();
      this.setInformationMode(false);
      this.setMessage(this.formMessage, '', false);
      this.saveState();
      const target = this.countryDropdown || this.country || this.shipping;
      requestAnimationFrame(() => {
        if (target && target.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (this.countryTrigger) this.countryTrigger.focus({ preventScroll: true });
      });
    }

    field(name) {
      return this.infoForm ? this.infoForm.querySelector(`[data-sf-field="${name}"]`) : null;
    }

    async goToSecureCheckout() {
      if (!this.infoForm || this.isSubmitting) return;
      this.clearMessage(this.formMessage);

      const invalid = this.validateInformationForm();
      if (invalid) {
        invalid.reportValidity();
        invalid.focus();
        return;
      }

      if (!this.country.value) {
        this.setMessage(this.formMessage, 'Choose a delivery country before continuing.', true);
        return;
      }
      if (!this.isAllowedCountry(this.country.value)) {
        this.setMessage(this.formMessage, 'This delivery country is not available.', true);
        return;
      }
      const selectedCountryCode = this.selectedCountryCode();
      if (this.activeCountryCodes.size && (!selectedCountryCode || !this.activeCountryCodes.has(selectedCountryCode))) {
        this.setMessage(this.formMessage, 'This delivery country is not enabled in Shopify Markets.', true);
        return;
      }
      const storefrontCountryCode = String(
        (window.Shopify && window.Shopify.country) ||
        this.currentCountryCode ||
        ''
      ).toUpperCase();

      if (selectedCountryCode && selectedCountryCode !== storefrontCountryCode) {
        this.setMessage(this.formMessage, 'Updating your market and currency before secure payment…', false);
        if (this.switchShopifyMarket(selectedCountryCode)) return;
      }

      this.captureCountryProfile(selectedCountryCode);
      this.saveState();

      const payButton = this.infoForm.querySelector('[data-sf-pay]');
      const originalLabel = payButton ? payButton.textContent : 'Continue to secure payment';
      this.isSubmitting = true;
      this.infoForm.setAttribute('aria-busy', 'true');
      if (payButton) {
        payButton.disabled = true;
        payButton.textContent = this.usesKustom() ? 'Preparing secure payment…' : 'Opening secure payment…';
      }
      this.saveState();

      try {
        await this.refreshCart();
        if (!this.cart.items || !this.cart.items.length) throw new Error('Your basket is empty.');

        const shippingConfirmed = await this.confirmShippingBeforePayment();
        if (this.usesKustom() && !this.kustomMockMode && this.cart.requires_shipping !== false && !shippingConfirmed) {
          throw new Error('We could not confirm the final delivery rate. Please check the postal code and address details.');
        }

        if (this.marketing && this.marketing.checked) {
          this.subscribeNewsletter(this.field('email').value.trim());
        }

        if (this.usesKustom()) {
          try {
            await this.startKustomCheckout();
            if (payButton) {
              payButton.disabled = true;
              payButton.textContent = 'Secure payment ready';
            }
            return;
          } catch (kustomError) {
            if (!this.kustomFallback) throw kustomError;
            this.setMessage(this.formMessage, 'Kustom is temporarily unavailable. Opening Shopify secure payment instead.', false);
          }
        }

        this.openShopifyCheckout();
      } catch (error) {
        this.isSubmitting = false;
        this.infoForm.removeAttribute('aria-busy');
        this.setMessage(this.formMessage, error.message || 'Unable to open secure payment.', true);
        if (payButton) {
          payButton.disabled = false;
          payButton.textContent = originalLabel.trim() || 'Continue to secure payment';
        }
      }
    }

    validateInformationForm() {
      if (!this.infoForm) return null;
      const requiredFields = Array.from(this.infoForm.querySelectorAll('input[required], select[required]'));
      const invalidNative = requiredFields.find((field) => !field.checkValidity());
      if (invalidNative) return invalidNative;

      const phone = this.field('phone');
      if (phone) {
        const countryMismatch = this.validatePhoneCountryMatch();
        if (countryMismatch) return countryMismatch;

        const digits = phone.value.replace(/\D/g, '');
        phone.setCustomValidity(digits.length >= 6 && digits.length <= 20 ? '' : 'Enter a valid mobile phone number.');
        if (!phone.checkValidity()) return phone;
      }
      return null;
    }

    usesKustom() {
      return this.checkoutEngine === 'kustom' || this.kustomMockMode;
    }

    openShopifyCheckout() {
      const params = this.checkoutParams();
      const simpleCart = this.cart.items.every((item) => {
        const properties = item.properties && Object.keys(item.properties).some((key) => item.properties[key] != null && String(item.properties[key]).trim() !== '');
        return !properties && !item.selling_plan_allocation && !item.selling_plan && !item.parent_relationship;
      });

      if (simpleCart) {
        const lines = this.cart.items.map((item) => `${item.variant_id}:${item.quantity}`).join(',');
        window.location.assign(`${this.routesRoot}cart/${lines}?${params.toString()}`);
        return;
      }

      this.submitCurrentCartToCheckout(params);
    }

    async startKustomCheckout() {
      if (!this.kustom || !this.kustomMount) throw new Error('Kustom checkout container is not available.');

      this.kustom.hidden = false;
      this.kustomMount.innerHTML = '';
      if (this.kustomStatus) this.kustomStatus.textContent = 'Preparing Kustom secure payment…';
      if (this.kustomBack) this.kustomBack.hidden = true;

      if (this.kustomMockMode) {
        this.renderKustomMock();
        return;
      }

      if (!this.kustomProxyUrl || !/^https:\/\//i.test(this.kustomProxyUrl)) {
        throw new Error('Kustom secure proxy is not configured yet.');
      }

      const response = await fetch(this.kustomProxyUrl, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(this.kustomPayload())
      });

      let data = {};
      try {
        data = await response.json();
      } catch (error) {
        throw new Error('Kustom proxy returned an unreadable response.');
      }

      if (!response.ok) {
        throw new Error(data.error || data.message || `Kustom checkout could not be started (${response.status}).`);
      }

      const snippet = data.html_snippet || (data.kustom && data.kustom.html_snippet);
      const orderId = data.order_id || (data.kustom && data.kustom.order_id) || '';
      if (!snippet) throw new Error('Kustom checkout did not return a checkout snippet.');

      this.kustomOrderId = orderId;
      try {
        if (orderId) sessionStorage.setItem('sf-kustom-order-id', orderId);
      } catch (error) {}

      this.renderKustomSnippet(snippet);
      if (this.kustomStatus) this.kustomStatus.textContent = '';
      if (this.kustomBack) this.kustomBack.hidden = false;
      requestAnimationFrame(() => this.kustom.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }

    provinceValue() {
      if (this.infoProvince && this.infoProvince.value) return this.infoProvince.value;
      if (this.province && this.province.value) return this.province.value;
      return '';
    }

    kustomPayload() {
      const rate = this.selectedRate || {};
      const countryName = this.country ? this.country.value : '';
      const normalizedCountry = this.normalizeCountryName(countryName);
      return {
        version: 'sf-kustom-v6',
        storefront: {
          host: window.location.host,
          url: window.location.href,
          locale: document.documentElement.lang || 'en',
          currency: this.el.dataset.currency || (this.cart && this.cart.currency) || 'SEK'
        },
        cart: {
          token: this.cart.token || '',
          item_count: this.cart.item_count || 0,
          items_subtotal_price: this.cart.items_subtotal_price || 0,
          total_discount: this.cart.total_discount || 0,
          total_price: this.cart.total_price || 0,
          items: (this.cart.items || []).map((item) => ({
            key: item.key,
            variant_id: item.variant_id,
            product_id: item.product_id,
            sku: item.sku || '',
            title: item.product_title || item.title || '',
            variant_title: item.variant_title || '',
            quantity: item.quantity,
            final_price: item.final_price,
            final_line_price: item.final_line_price,
            original_price: item.original_price,
            original_line_price: item.original_line_price,
            requires_shipping: item.requires_shipping !== false,
            taxable: item.taxable !== false,
            properties: item.properties || {}
          }))
        },
        customer: {
          email: this.field('email').value.trim(),
          first_name: this.field('first_name').value.trim(),
          last_name: this.field('last_name').value.trim(),
          phone: this.field('phone').value.trim()
        },
        shipping_address: {
          first_name: this.field('first_name').value.trim(),
          last_name: this.field('last_name').value.trim(),
          address1: this.field('address1').value.trim(),
          address2: this.field('address2').value.trim(),
          city: this.field('city').value.trim(),
          postal_code: this.field('zip').value.trim(),
          country: countryName,
          country_code: this.countryIsoMap[normalizedCountry] || '',
          province: this.provinceValue()
        },
        shipping: {
          name: rate.presentment_name || rate.name || '',
          code: rate.code || '',
          source: rate.source || '',
          price: rate.price || '',
          price_cents: this.shippingCents || 0,
          carrier_identifier: rate.carrier_identifier || ''
        },
        discount_code: (this.discountInput && this.discountInput.value.trim()) || this.discountCode || '',
        marketing_opt_in: Boolean(this.marketing && this.marketing.checked)
      };
    }

    renderKustomSnippet(html) {
      this.kustomMount.innerHTML = html;
      const scripts = Array.from(this.kustomMount.querySelectorAll('script'));
      scripts.forEach((oldScript) => {
        const script = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attribute) => script.setAttribute(attribute.name, attribute.value));
        script.text = oldScript.text || oldScript.textContent || '';
        oldScript.parentNode.replaceChild(script, oldScript);
      });
    }

    renderKustomMock() {
      const shippingName = this.selectedRate ? (this.selectedRate.presentment_name || this.selectedRate.name || 'Selected delivery') : 'No shipping required';
      const shippingPrice = this.shippingCents === 0 ? 'Free' : this.money(this.shippingCents);
      const total = Number(this.cart.total_price || 0) + Number(this.shippingCents || 0);
      this.kustomMount.innerHTML = `
        <div class="sf-kustom-mock" role="region" aria-label="Kustom integration preview">
          <div class="sf-kustom-mock__eyebrow">Integration preview — no payment will be created</div>
          <div class="sf-kustom-mock__shipping"><span>${this.escape(shippingName)}</span><strong>${this.escape(shippingPrice)}</strong></div>
          <div class="sf-kustom-mock__methods">
            <span>Klarna</span><span>Card</span><span>Apple Pay</span><span>Google Pay</span>
          </div>
          <div class="sf-kustom-mock__total"><span>Total</span><strong>${this.money(total)}</strong></div>
          <p>This safe preview confirms that the Stockholm checkout is ready to mount the real Kustom Checkout snippet once the secure proxy and merchant credentials are connected.</p>
        </div>`;
      if (this.kustomStatus) this.kustomStatus.textContent = '';
      if (this.kustomBack) this.kustomBack.hidden = false;
      requestAnimationFrame(() => this.kustom.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }

    resetKustomCheckout() {
      if (!this.kustom || !this.kustomMount) return;
      this.kustomMount.innerHTML = '';
      this.kustom.hidden = true;
      this.kustomOrderId = '';
      if (this.kustomStatus) this.kustomStatus.textContent = '';
      if (this.kustomBack) this.kustomBack.hidden = true;
      const payButton = this.infoForm && this.infoForm.querySelector('[data-sf-pay]');
      if (payButton) {
        payButton.disabled = false;
        payButton.textContent = 'Continue to secure payment';
      }
      this.isSubmitting = false;
      if (this.infoForm) this.infoForm.removeAttribute('aria-busy');
      try { sessionStorage.removeItem('sf-kustom-order-id'); } catch (error) {}
      requestAnimationFrame(() => this.infoForm.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }

    checkoutParams() {
      const params = new URLSearchParams();
      const email = this.field('email').value.trim();
      const firstName = this.field('first_name').value.trim();
      const lastName = this.field('last_name').value.trim();
      const address1 = this.field('address1').value.trim();
      const address2 = this.field('address2').value.trim();
      const city = this.field('city').value.trim();
      const zip = this.field('zip').value.trim();
      const phone = this.field('phone').value.trim();

      params.set('checkout[email]', email);
      params.set('checkout[shipping_address][first_name]', firstName);
      params.set('checkout[shipping_address][last_name]', lastName);
      params.set('checkout[shipping_address][address1]', address1);
      if (address2) params.set('checkout[shipping_address][address2]', address2);
      params.set('checkout[shipping_address][city]', city);
      params.set('checkout[shipping_address][country]', this.country.value);
      params.set('checkout[shipping_address][zip]', zip);
      const province = this.provinceValue();
      if (province) params.set('checkout[shipping_address][province]', province);

      // Shopify's documented cart-permalink list doesn't currently mention phone,
      // but this parameter is harmless if a checkout ignores it.
      if (phone) params.set('checkout[shipping_address][phone]', phone);

      const code = (this.discountInput && this.discountInput.value.trim()) || this.discountCode;
      if (code) params.set('discount', code);
      params.set('attributes[sf_checkout_flow]', 'stockholm_caia_v1');
      if (this.selectedRate) params.set('attributes[sf_estimated_shipping]', this.selectedRate.presentment_name || this.selectedRate.name || 'Selected rate');
      return params;
    }

    submitCurrentCartToCheckout(params) {
      const form = document.createElement('form');
      form.method = 'post';
      form.action = `${this.routesRoot}cart`;
      form.style.display = 'none';

      params.forEach((value, key) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      const checkout = document.createElement('input');
      checkout.type = 'hidden';
      checkout.name = 'checkout';
      checkout.value = 'Checkout';
      form.appendChild(checkout);

      document.body.appendChild(form);
      form.submit();
    }

    subscribeNewsletter(email) {
      if (!email) return;
      try {
        const data = new FormData();
        data.append('form_type', 'customer');
        data.append('utf8', '✓');
        data.append('contact[tags]', 'newsletter');
        data.append('contact[email]', email);
        fetch(`${this.routesRoot}contact`, {
          method: 'POST',
          credentials: 'same-origin',
          body: data,
          keepalive: true
        }).catch(() => {});
      } catch (error) {
        // Newsletter opt-in must never block checkout.
      }
    }

    async applyDiscount() {
      const code = this.discountInput ? this.discountInput.value.trim() : '';
      if (!code) {
        this.setMessage(this.discountMessage, 'Enter a discount code.', true);
        return;
      }

      this.clearMessage(this.discountMessage);
      const previousText = this.discountApply ? this.discountApply.textContent : 'Apply';
      const previousDiscount = Number((this.cart && this.cart.total_discount) || 0);
      if (this.discountApply) {
        this.discountApply.disabled = true;
        this.discountApply.setAttribute('aria-busy', 'true');
        this.discountApply.textContent = 'Applying…';
      }

      try {
        const response = await fetch(`${this.routesRoot}cart/update.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ discount: code })
        });
        const payload = await this.safeJson(response);
        if (!response.ok) {
          throw new Error(this.flattenErrors(payload) || 'This discount code could not be applied.');
        }

        this.cart = payload || this.cart;
        const status = this.discountCodeStatus(this.cart, code, previousDiscount);
        this.discountCode = status.invalid ? '' : code;
        this.renderBasket();
        this.resetShippingAfterCartChange();
        this.saveState();

        if (status.invalid) {
          this.setMessage(this.discountMessage, 'This discount code is not valid for the current basket.', true);
        } else if (status.applied) {
          this.setMessage(this.discountMessage, 'Discount applied.', false);
        } else {
          this.setMessage(this.discountMessage, 'Code saved. Shopify will confirm eligibility at secure payment.', false);
        }
      } catch (error) {
        this.setMessage(this.discountMessage, error.message || 'This discount code could not be applied.', true);
      } finally {
        if (this.discountApply) {
          this.discountApply.disabled = false;
          this.discountApply.removeAttribute('aria-busy');
          this.discountApply.textContent = previousText;
        }
      }
    }

    discountCodeStatus(cart, code, previousDiscount = 0) {
      const normalized = String(code || '').trim().toLowerCase();
      const codeLists = [];
      if (Array.isArray(cart && cart.discount_codes)) codeLists.push(...cart.discount_codes);
      if (Array.isArray(cart && cart.discountCodes)) codeLists.push(...cart.discountCodes);
      const explicit = codeLists.find((entry) => {
        const candidate = typeof entry === 'string' ? entry : (entry && (entry.code || entry.title));
        return String(candidate || '').trim().toLowerCase() === normalized;
      });
      if (explicit && typeof explicit === 'object' && explicit.applicable === false) {
        return { applied: false, invalid: true };
      }
      if (explicit && (typeof explicit !== 'object' || explicit.applicable !== false)) {
        return { applied: explicit.applicable !== false, invalid: false };
      }

      const applications = [];
      if (Array.isArray(cart && cart.cart_level_discount_applications)) applications.push(...cart.cart_level_discount_applications);
      (cart && Array.isArray(cart.items) ? cart.items : []).forEach((item) => {
        if (Array.isArray(item.line_level_discount_allocations)) {
          item.line_level_discount_allocations.forEach((allocation) => {
            if (allocation && allocation.discount_application) applications.push(allocation.discount_application);
          });
        }
      });
      const titleMatch = applications.some((application) =>
        String((application && (application.code || application.title)) || '').trim().toLowerCase() === normalized
      );
      const currentDiscount = Number((cart && cart.total_discount) || 0);
      return {
        applied: titleMatch || currentDiscount > previousDiscount,
        invalid: false
      };
    }

    saveState() {
      const fields = {};
      ['email', 'first_name', 'last_name'].forEach((name) => {
        const input = this.field(name);
        if (input) fields[name] = input.value;
      });

      const currentCode = this.deliveryCountryCode || this.selectedCountryCode();
      if (currentCode) this.captureCountryProfile(currentCode);

      const state = {
        ts: Date.now(),
        country: this.country ? this.country.value : '',
        shippingRequiresPostal: this.shippingRequiresPostal,
        shippingRequiresProvince: this.shippingRequiresProvince,
        marketing: this.marketing ? this.marketing.checked : false,
        discountCode: this.discountInput ? this.discountInput.value : this.discountCode,
        infoVisible: this.info ? !this.info.hidden : false,
        fields
      };
      try {
        sessionStorage.setItem(this.stateKey, JSON.stringify(state));
      } catch (error) {}
    }

    restoreState() {
      let state = null;
      try {
        state = JSON.parse(sessionStorage.getItem(this.stateKey) || 'null');
      } catch (error) {
        state = null;
      }
      if (!state || !state.ts || Date.now() - state.ts > 30 * 60 * 1000) {
        if (this.country) this.country.value = '';
        if (this.shipping) this.shipping.hidden = true;
        return;
      }

      if (this.country && state.country) {
        const option = Array.from(this.country.options).find((entry) => entry.value === state.country);
        if (option) {
          this.country.value = state.country;
          this.setupProvinceOptions();
          this.shippingRequiresProvince = false;
          this.shippingRequiresPostal = false;
          if (this.provinceWrap) this.provinceWrap.hidden = true;
          if (this.shippingZipWrap) this.shippingZipWrap.hidden = true;
          this.shipping.hidden = false;
          // After a market reload, ask Shopify for the actual delivery methods
          // again. This is what lets a configured carrier such as UPS/DHL appear
          // instead of leaving the provisional Standard delivery card in place.
          window.setTimeout(() => this.calculateShipping(), 0);
        }
      }

      if (this.marketing) this.marketing.checked = Boolean(state.marketing);
      if (this.discountInput && state.discountCode) this.discountInput.value = state.discountCode;
      this.discountCode = state.discountCode || '';

      // Identity fields are global; delivery address and phone are stored per country.
      if (this.infoForm && state.fields) {
        ['email', 'first_name', 'last_name'].forEach((name) => {
          const input = this.field(name);
          if (input) input.value = state.fields[name] || '';
        });
      }

      this.deliveryCountryCode = this.selectedCountryCode();
      if (this.deliveryCountryCode) {
        this.restoreCountryProfile(this.deliveryCountryCode);
      } else {
        this.clearCountryProfileFields('');
      }

      if (this.info && state.infoVisible) {
        this.setInformationMode(true);
        if (this.countryNote && this.country && this.country.value) {
          this.countryNote.textContent = `Delivery to ${this.country.value}`;
        }
      } else {
        this.setInformationMode(false);
      }
    }

    setMessage(node, text, error = false) {
      if (!node) return;
      node.textContent = text || '';
      node.classList.toggle('is-error', Boolean(error));
    }

    clearMessage(node) {
      this.setMessage(node, '', false);
    }

    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }
  }

  new StockholmCaiaCheckout(root);
})();
